<template>
    <ValidatorMessage
        :message="null"
        class="border"
    >
        <DropdownSelect
            class="flex-fill"
            :data="computedCountries"
            v-model="selectedCountry"
            :showSearch="true"
            :favorites="[
                WellKnown.COUNTRY_NO,
                WellKnown.COUNTRY_FI,
                WellKnown.COUNTRY_SE,
                WellKnown.COUNTRY_GE,
                WellKnown.COUNTRY_FR,
                WellKnown.COUNTRY_IT,
            ]"
        />
    </ValidatorMessage>
</template>
<script setup lang="ts">
import { watch, computed, ref, type ComponentPublicInstance } from 'vue';
import type { IIcon } from '../Interfaces';
import ValidatorMessage from './ValidatorMessage.vue';
import { type IEditorComponent } from '@/objects/gui';
import { useLoaders, useTaskLoader } from '@/composeables';
import { DropdownSelect } from '@/components';
import { useCommonStoreRef } from '@/stores';
import type { SelectLineValueType } from '@/objects';
import { $tm } from '@/locize';
import WellKnown from '@/objects/generated/static/WellKnown';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<
        {
            /**
             * Modelvalue can be either the id or the iso2 code of the country
             * @remarks this is for legacy reasons since we use the same for Nationality and Country
             */
            modelValue: number | string | null;

            /**
             * Should we autofocus the input
             */
            autofocus?: boolean;

            /**
             * If the input is readonly
             */
            readonly?: boolean;

            /**
             * If the input is disabled
             */
            disabled?: boolean;
        } & IIcon
    >(),
    {},
);

const { Countries } = useCommonStoreRef();

/**
 * Just make sure we have the countries loaded
 */
const { AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['COUNTRIES']);

/**
 * Check if the modelvalue is a number
 * If not then we will return ISO2 code
 */
const modelValueIsNumber = typeof props.modelValue === 'number' && !isNaN(props.modelValue);

/**
 * Return a list of phonenumber prefix for the dropdown
 */
const computedCountries = computed(() => {
    // Map countries to selectlines and run translate on them and then order them alphabetically
    let countries = Countries.value
        .map((c) => {
            return {
                value: c.ID,
                text: $tm(c.TranslationKey ?? ''),
                icon: `fi-${c.ISO2?.toLowerCase()}`,
            };
        })
        .sort((a, b) => a.text.localeCompare(b.text));

    return countries;
});

const findSelectedCountry = () => {
    if (!props.modelValue) {
        return 160; // We default to norway, but should be defaulted to the event federation country
    }

    // Check if modelValue is a number
    if (typeof props.modelValue === 'number') {
        return props.modelValue;
    } else {
        // We assume it is a iso2 code and try and find the country
        let country = Countries.value.find((c) => c.ISO2 == props.modelValue);
        if (country) {
            return country.ID;
        }

        // We return the unknown country or No Nation
        return 999;
    }
};

const selectedCountry = ref<SelectLineValueType | null>(findSelectedCountry());

// The ref of the input element
const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

const emit = defineEmits<{
    (e: 'update:modelValue', modelValue: number | string | null): void;
}>();

// We emit the selected country when it changes
watch(
    selectedCountry,
    /**
     * @remarks We need to check if the modelValue is a number or a string and then emit the correct value. This is for legacy reasons and that some fields in the database is ISO2 and some is ID
     * @param newVal
     */
    (newVal) => {
        if (modelValueIsNumber) {
            emit('update:modelValue', Number(newVal));
        } else {
            let country = Countries.value.find((c) => c.ID == newVal);
            if (country) {
                emit('update:modelValue', country.ISO2);
            } else {
                emit('update:modelValue', null);
            }
        }
    },
);

/**
 * This will update the component when the modelValue changes externally
 */
watch(
    () => props.modelValue,
    (newVal) => {
        selectedCountry.value = findSelectedCountry();
    },
);

defineExpose<IEditorComponent>({ focus: () => inputref.value?.focus(), validateMessage: computed(() => null) });
</script>
